import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { getUserById, followUser, unfollowUser, isFollowing, getFollowers, getFollowing } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { User, Calendar, MapPin, Award, ArrowLeft, UserPlus, UserMinus, Users, Activity, Lock, X, ChevronRight, Zap, Shield, Target, UserCircle } from 'lucide-react';
import { DotLoader } from 'react-spinners';
import defaultAvatar from '../assets/default-avatar.jpg'
import Footer from '../components/Footer';

function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchFollowCounts();
  }, [id]);

  useEffect(() => {
    if (isAuthenticated && user && id && user._id !== id) {
      checkFollowing();
    }
  }, [isAuthenticated, user, id]);

  const fetchProfile = async () => {
    try {
      const data = await getUserById(id);
      setProfile(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      setLoading(false);
    }
  };

  const checkFollowing = async () => {
    try {
      const { isFollowing: followingStatus } = await isFollowing(id);
      setFollowing(followingStatus);
    } catch (error) {
      console.error('Erreur lors de la vérification:', error);
    }
  };

  const fetchFollowCounts = async () => {
    try {
      const [followers, following] = await Promise.all([
        getFollowers(id),
        getFollowing(id)
      ]);
      setFollowersCount(followers.length);
      setFollowingCount(following.length);
    } catch (error) {
      console.error('Erreur lors du chargement des compteurs:', error);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (following) {
        await unfollowUser(id);
        setFollowing(false);
        setFollowersCount(prev => prev - 1);
      } else {
        await followUser(id);
        setFollowing(true);
        setFollowersCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const fetchFollowersList = async () => {
    try {
      const data = await getFollowers(id);
      setFollowersList(data);
      setShowFollowers(true);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleImageClick = (e) => {
    e.preventDefault();
    if (profile.photoProfil) {
      setShowImageModal(true);
    }
  };

  const fetchFollowingList = async () => {
    try {
      const data = await getFollowing(id);
      setFollowingList(data);
      setShowFollowing(true);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const getInitials = () => {
    return `${profile.prenom?.charAt(0) || ''}${profile.nom?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (date) => {
    if (!date) return 'Non renseigné';
    return new Date(date).toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatPace = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isOwnProfile = isAuthenticated && user && user._id === id;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <p className="text-xl text-gray-400">Profil introuvable</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Bouton retour - Sticky sur mobile */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-4 sm:mb-6 sticky top-20 sm:static z-10 bg-slate-800 sm:bg-transparent px-3 py-2 sm:p-0 rounded-lg sm:rounded-none shadow-md sm:shadow-none border border-slate-700 sm:border-0 transition"
        >
          <ArrowLeft size={20} className="mr-2" />
          <span>Retour</span>
        </button>

        <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg overflow-hidden">
  {/* Header avec photo de profil */}
  <div className="pt-8 px-4 sm:px-8">
    {/* Photo de profil centrée */}
    <div className="flex justify-center mb-6">
      <div 
        className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-slate-700 shadow-xl cursor-pointer hover:border-indigo-500 transition-all duration-300 hover:scale-105"
        onClick={() => setIsImageModalOpen(true)}
      >
        {profile.photoProfil ? (
          <img
            src={profile.photoProfil}
            alt={`${profile.prenom} ${profile.nom}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={defaultAvatar}
            alt={`${profile.prenom} ${profile.nom}`}
            className="w-full h-full object-cover"
          />
        )}
      </div>
    </div>

    {/* Informations du profil */}
    <div className="text-center mb-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
        {profile.prenom} {profile.nom}
      </h1>
      {profile.ville && (
        <p className="flex items-center justify-center text-sm sm:text-base text-gray-400 mb-4">
          <MapPin size={16} className="mr-1.5 flex-shrink-0" />
          {profile.ville}
        </p>
      )}

      {/* Bouton Follow/Unfollow */}
      {!isOwnProfile && isAuthenticated && (
        <button
          onClick={handleFollow}
          className={`inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-2.5 rounded-lg font-semibold transition shadow-lg ${
            following
              ? 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
              : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-indigo-500/20'
          }`}
        >
          {following ? (
            <>
              <UserMinus size={18} />
              <span className="hidden sm:inline">Se désabonner</span>
              <span className="sm:hidden">Abonné</span>
            </>
          ) : (
            <>
              <UserPlus size={18} />
              <span>Suivre</span>
            </>
          )}
        </button>
      )}
    </div>

    {/* Stats Follow - Centrées */}
    <div className="flex items-center justify-center gap-8 sm:gap-12 pb-6 border-t border-slate-700 pt-6">
      <button
        onClick={fetchFollowersList}
        className="flex flex-col items-center gap-1 hover:opacity-80 transition"
      >
        <span className="text-2xl sm:text-3xl font-bold text-white">{followersCount}</span>
        <span className="text-xs sm:text-sm text-gray-400">Abonné{followersCount > 1 ? 's' : ''}</span>
      </button>

      <div className="h-12 w-px bg-slate-700"></div>

      <button
        onClick={fetchFollowingList}
        className="flex flex-col items-center gap-1 hover:opacity-80 transition"
      >
        <span className="text-2xl sm:text-3xl font-bold text-white">{followingCount}</span>
        <span className="text-xs sm:text-sm text-gray-400">Abonnement{followingCount > 1 ? 's' : ''}</span>
      </button>

      <div className="h-12 w-px bg-slate-700"></div>

      {/* 🆕 Points */}
      <button
        onClick={() => navigate('/global-leaderboard')}
        className="flex flex-col items-center gap-1 hover:opacity-80 transition group"
      >
        <span className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent group-hover:scale-110 transition-transform">
          {profile.challengePoints || 0}
        </span>
        <div className="flex flex-col items-center">
          <span className="text-xs sm:text-sm text-yellow-400 font-medium">🏆 Points</span>
          <span className="text-[9px] sm:text-[10px] text-yellow-400/70 group-hover:text-yellow-400 transition-colors">Classement</span>
        </div>
      </button>
    </div>
  </div>
</div>

       {/* Informations détaillées - Cards séparées */}
        <div className="mt-6 space-y-4 sm:space-y-6">
          {/* Section Bio - MAINTENANT AU TOP LEVEL */}
          {profile.bio && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-4 sm:p-6">
              <div className="flex items-center space-x-2 mb-3">
                <User size={18} className="text-indigo-500" />
                <h2 className="text-lg sm:text-xl font-bold text-white">À propos</h2>
              </div>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                {profile.bio}
              </p>
            </div>
          )}

          {/* Informations personnelles - SANS IMBRICATION */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <User size={20} className="text-indigo-500" />
              Informations personnelles
            </h2>

            <div className="space-y-3">
              {/* Âge */}
              {profile.dateNaissance && (
                <div className="flex items-start gap-3">
                  <Calendar size={18} className="text-gray-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-400">Âge</p>
                    <p className="text-sm sm:text-base text-white font-semibold">
                      {new Date().getFullYear() - new Date(profile.dateNaissance).getFullYear()} ans
                    </p>
                  </div>
                </div>
              )}

              {/* Ville */}
              {profile.ville && (
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-gray-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-400">Ville</p>
                    <p className="text-sm sm:text-base text-white font-medium truncate">
                      {profile.ville}
                    </p>
                  </div>
                </div>
              )}

              {/* Genre */}
              {profile.genre && profile.genre !== 'Non spécifié' && (
                <div className="flex items-start gap-3">
                  <User size={18} className="text-gray-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-400">Genre</p>
                    <p className="text-sm sm:text-base text-white font-medium">
                      {profile.genre}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Section Profil sportif - MAINTENANT AU TOP LEVEL */}
          {(profile.niveauCourse || profile.objectifs) && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-4 sm:p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Target size={18} className="text-indigo-500" />
                <h2 className="text-lg sm:text-xl font-bold text-white">Profil sportif</h2>
              </div>
              
              <div className="space-y-4">
                {/* Niveau */}
                {profile.niveauCourse && profile.niveauCourse !== 'Non spécifié' && (
                  <div className="flex items-start space-x-3">
                    <Award size={16} className="text-gray-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-gray-400 font-medium mb-1">Niveau</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs sm:text-sm font-bold ${
                        profile.niveauCourse === 'Débutant' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                        profile.niveauCourse === 'Intermédiaire' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                        'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                      }`}>
                        {profile.niveauCourse}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Objectifs */}
                {profile.objectifs && (
                  <div className="flex items-start space-x-3">
                    <Target size={16} className="text-gray-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-gray-400 font-medium mb-1">Objectifs</p>
                      <p className="text-sm sm:text-base text-gray-300">{profile.objectifs}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section VDOT - Avec icônes modernes */}
          {profile.vdot && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Zap size={20} className="text-yellow-500" />
                Niveau de course
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {/* VDOT Score */}
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={18} className="text-indigo-400" />
                    <p className="text-xs sm:text-sm text-gray-400 font-medium">VDOT</p>
                  </div>
                  <p className="text-2xl sm:text-3xl font-black text-white">
                    {profile.vdot}
                  </p>
                </div>

                {/* Niveau */}
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={18} className="text-green-400" />
                    <p className="text-xs sm:text-sm text-gray-400 font-medium">Niveau</p>
                  </div>
                  <p className="text-lg sm:text-xl font-bold text-white capitalize">
                    {profile.niveau || 'Non défini'}
                  </p>
                </div>

                {/* Distance préférée */}
                {profile.distancePreferee && (
                  <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 p-4 rounded-xl sm:col-span-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity size={18} className="text-orange-400" />
                      <p className="text-xs sm:text-sm text-gray-400 font-medium">Distance</p>
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-white">
                      {profile.distancePreferee}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Allures d'entraînement - Si visibles */}
          {profile.paces && profile.pacesVisibles && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Award size={20} className="text-indigo-500" />
                Allures d'entraînement
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/30 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1 font-medium">EF</p>
                  <p className="text-sm sm:text-base font-black text-green-400">
                    {formatPace(profile.paces.easyMin)} - {formatPace(profile.paces.easyMax)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/30 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1 font-medium">Marathon</p>
                  <p className="text-sm sm:text-base font-black text-blue-400">
                    {formatPace(profile.paces.marathon)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border border-yellow-500/30 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1 font-medium">Seuil</p>
                  <p className="text-sm sm:text-base font-black text-yellow-400">
                    {formatPace(profile.paces.threshold)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/10 border border-orange-500/30 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1 font-medium">Intervalle</p>
                  <p className="text-sm sm:text-base font-black text-orange-400">
                    {formatPace(profile.paces.interval)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-red-500/10 to-red-600/10 border border-red-500/30 p-3 rounded-xl col-span-2 sm:col-span-1">
                  <p className="text-xs text-gray-400 mb-1 font-medium">Répétition</p>
                  <p className="text-sm sm:text-base font-black text-red-400">
                    {formatPace(profile.paces.repetition)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Message si paces privées */}
          {profile.vdot && profile.paces && !profile.pacesVisibles && !isOwnProfile && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-4 sm:p-6">
              <div className="flex flex-col items-center justify-center p-6 sm:p-8 bg-slate-900 border border-slate-700 rounded-xl">
                <div className="bg-slate-800 p-3 rounded-full mb-3">
                  <Lock className="text-gray-500" size={24} />
                </div>
                <p className="text-sm sm:text-base text-gray-400 text-center font-medium">
                  Les allures d'entraînement de {profile.prenom} sont privées
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal Followers - Style sombre */}
      {showFollowers && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-70"
            onClick={() => setShowFollowers(false)}
          ></div>
          <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-4 sm:px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg sm:text-xl font-bold text-white">Abonnés ({followersCount})</h3>
              <button
                onClick={() => setShowFollowers(false)}
                className="text-gray-400 hover:text-gray-300 p-2 hover:bg-slate-700 rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-64px)]">
              {followersList.length > 0 ? (
                <div className="space-y-2">
                  {followersList.map((follower) => (
                    <button
                      key={follower._id}
                      onClick={() => {
                        setShowFollowers(false);
                        navigate(`/user/${follower._id}`);
                      }}
                      className="w-full flex items-center space-x-3 p-3 hover:bg-slate-700 rounded-xl transition active:scale-95"
                    >
                      {follower.photoProfil ? (
                        <img
                          src={follower.photoProfil}
                          alt={`${follower.prenom} ${follower.nom}`}
                          className="w-12 h-12 rounded-full object-cover border-2 border-slate-600"
                        />
                      ) : (
                        <img
                          src={defaultAvatar}
                          alt={`${follower.prenom} ${follower.nom}`}
                          className="w-12 h-12 rounded-full object-cover border-2 border-slate-600"
                        />
                      )}
                      <div className="flex-1 text-left min-w-0">
                        <p className="font-semibold text-white truncate">
                          {follower.prenom} {follower.nom}
                        </p>
                        {follower.ville && (
                          <p className="text-sm text-gray-400 truncate">{follower.ville}</p>
                        )}
                      </div>
                      <ChevronRight size={18} className="text-gray-500 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-sm">Aucun abonné</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Following - Style sombre */}
      {showFollowing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-70"
            onClick={() => setShowFollowing(false)}
          ></div>
          <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-4 sm:px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg sm:text-xl font-bold text-white">Abonnements ({followingCount})</h3>
              <button
                onClick={() => setShowFollowing(false)}
                className="text-gray-400 hover:text-gray-300 p-2 hover:bg-slate-700 rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-64px)]">
              {followingList.length > 0 ? (
                <div className="space-y-2">
                  {followingList.map((followedUser) => (
                    <button
                      key={followedUser._id}
                      onClick={() => {
                        setShowFollowing(false);
                        navigate(`/user/${followedUser._id}`);
                      }}
                      className="w-full flex items-center space-x-3 p-3 hover:bg-slate-700 rounded-xl transition active:scale-95"
                    >
                      {followedUser.photoProfil ? (
                        <img
                          src={followedUser.photoProfil}
                          alt={`${followedUser.prenom} ${followedUser.nom}`}
                          className="w-12 h-12 rounded-full object-cover border-2 border-slate-600"
                        />
                      ) : (
                        <img
                          src={defaultAvatar}
                          alt={`${followedUser.prenom} ${followedUser.nom}`}
                          className="w-12 h-12 rounded-full object-cover border-2 border-slate-600"
                        />
                      )}
                      <div className="flex-1 text-left min-w-0">
                        <p className="font-semibold text-white truncate">
                          {followedUser.prenom} {followedUser.nom}
                        </p>
                        {followedUser.ville && (
                          <p className="text-sm text-gray-400 truncate">{followedUser.ville}</p>
                        )}
                      </div>
                      <ChevronRight size={18} className="text-gray-500 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-sm">Aucun abonnement</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <Footer />
      {showImageModal && profile.photoProfil && (
              <div 
                className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
                onClick={() => setShowImageModal(false)}
              >
                {/* Bouton fermer */}
                <button
                  onClick={() => setShowImageModal(false)}
                  className="absolute top-4 right-4 z-[10000] bg-white/10 hover:bg-white/20 backdrop-blur-md text-white p-3 rounded-full transition-all hover:scale-110 active:scale-95 shadow-2xl border border-white/20"
                  aria-label="Fermer"
                >
                  <X size={24} />
                </button>
      
                {/* Image */}
                <div 
                  className="relative max-w-7xl max-h-[85vh] w-full h-full flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  
                    <img
                      src={profile.photoProfil}
                      alt={`${profile.prenom} ${profile.nom}`}
                      className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-slate-800 object-cover shadow-xl"
                    />

                </div>
      
                {/* Instructions en bas */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[10000]">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-2 shadow-2xl">
                    <p className="text-white text-sm font-medium">Cliquez n'importe où pour fermer</p>
                  </div>
                </div>
              </div>
            )}
            {isImageModalOpen && profile.photoProfil && (
              <div 
                className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                onClick={() => setIsImageModalOpen(false)}
              >
                <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
                  <button
                    onClick={() => setIsImageModalOpen(false)}
                    className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2 transition z-10"
                  >
                    <X size={24} />
                  </button>
                  <img
                    src={profile.photoProfil}
                    alt={`${profile.prenom} ${profile.nom}`}
                    className="max-w-full max-h-full object-contain rounded-lg"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}
    </div>
    
  );
}

export default UserProfile;